import { CheckedAddress, NormalizedNumber, promiseAll } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions, useSuspenseQuery } from '@tanstack/react-query'
import { Address } from 'viem'
import { z } from 'zod'

import { SPARK_VAULTS_V2_ENABLED } from '@/config/chain/flags'
import { spark2ApiUrl } from '@/config/consts'
import { normalizedNumberSchema } from '@/domain/common/validation'
import { season3Active } from '@/features/points/consts'
import { resolveActivePointsSeason, selectSeasonValue } from '@/features/points/helpers'
import { PointsType } from '@/features/points/types'
import { SuspenseQueryWith } from '@/utils/types'

export interface PointsDataQueryParams {
  account: CheckedAddress | undefined
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function pointsDataQueryOptions({ account }: PointsDataQueryParams) {
  return queryOptions({
    queryKey: pointsDataQueryKey({ account }),
    queryFn: () => pointsDataQueryFunction({ account }),
  })
}

export const baDataResponseSchema = z.object({
  invite_code: z.string(),
  referral_code: z.string().nullable(),
  invited_users: z.number(),
  leaderboard_position: z.number().nullable(),
  points: z.array(
    z.object({
      type: z.string(),
      amount_normalized: normalizedNumberSchema,
    }),
  ),
})

export function pointsDataQueryKey({ account }: { account: Address | undefined }): QueryKey {
  return ['points', 'pointsData', account]
}

export async function pointsDataQueryFunction({ account }: PointsDataQueryParams): Promise<PointsData> {
  if (!account) {
    return {
      referralCode: '',
      invitedUsers: 0,
      inviterCode: undefined,
      leaderboardPosition: null,
      points: [],
      referralPoints: NormalizedNumber.ZERO,
      season1TotalPoints: NormalizedNumber.ZERO,
      season2TotalPoints: NormalizedNumber.ZERO,
      season3TotalPoints: NormalizedNumber.ZERO,
      season1Points: [],
      season2Points: [],
      season3Points: [],
    }
  }

  const res = await fetch(`${spark2ApiUrl}/referrals/${account}/`)
  if (!res.ok) {
    throw new Error('Failed to fetch points')
  }

  const { season1, season2, season3 } = await promiseAll(
    {
      season1: async () => {
        const res = await fetch(`${spark2ApiUrl}/referrals/${account}/`)
        if (!res.ok) {
          throw new Error('Failed to fetch points')
        }

        const parsedResult = baDataResponseSchema.parse(await res.json())
        const points = parsedResult.points
          .filter((p) => ['pendle', 'referral', 'staking'].includes(p.type))
          .map(
            (points): PointsTypeAmountDetails => ({
              type: points.type as PointsType,
              amount: points.amount_normalized,
              campaignActive: true,
            }),
          )
          .sort(sortByPointsType)

        return {
          points,
          referralCode: parsedResult.invite_code.toUpperCase(),
          inviterCode: parsedResult.referral_code?.toUpperCase(),
          invitedUsers: parsedResult.invited_users,
          leaderboardPosition: parsedResult.leaderboard_position,
        }
      },
      season2: async () => {
        const res = await fetch(`${spark2ApiUrl}/referrals/${account}/?season=2`)
        if (!res.ok) {
          throw new Error('Failed to fetch points season 2')
        }

        const parsedResult = baDataResponseSchema.parse(await res.json())
        const points = parsedResult.points
          .filter((p) =>
            [
              'staking',
              'referral',
              'loyalty',
              'governance',
              'hold-susdc',
              ...(SPARK_VAULTS_V2_ENABLED ? ['hold-spusdc', 'hold-spusdt', 'hold-speth'] : []),
            ].includes(p.type),
          )
          .map(
            (points): PointsTypeAmountDetails => ({
              type: points.type as PointsType,
              amount: points.amount_normalized,
              campaignActive: true,
            }),
          )

        return {
          points: points.sort(sortByPointsType),
          leaderboardPosition: parsedResult.leaderboard_position,
        }
      },
      season3: async () => {
        if (!season3Active) {
          return {
            points: [],
            leaderboardPosition: null,
          }
        }

        const res = await fetch(`${spark2ApiUrl}/referrals/${account}/?season=3`)
        if (!res.ok) {
          throw new Error('Failed to fetch points season 3')
        }

        const parsedResult = baDataResponseSchema.parse(await res.json())
        const points = parsedResult.points
          .filter((p) =>
            [
              'staking',
              'referral',
              'loyalty',
              'governance',
              'hold-susdc',
              'hold-susds',
              ...(SPARK_VAULTS_V2_ENABLED ? ['hold-spusdc', 'hold-spusdt', 'hold-speth'] : []),
            ].includes(p.type),
          )
          .map(
            (points): PointsTypeAmountDetails => ({
              type: points.type as PointsType,
              amount: points.amount_normalized,
              campaignActive: points.type !== 'hold-susdc',
            }),
          )

        return {
          points: points.sort(sortByPointsType),
          leaderboardPosition: parsedResult.leaderboard_position,
        }
      },
    },
    { concurrency: 'unbounded' },
  )

  const activeSeason = resolveActivePointsSeason()
  const activeSeasonPoints = selectSeasonValue(activeSeason, {
    2: season2.points,
    3: season3.points,
  })
  const referralPoints = activeSeasonPoints.find((p) => p.type === 'referral')?.amount ?? NormalizedNumber.ZERO
  const season1TotalPoints = season1.points.reduce((acc, curr) => acc.plus(curr.amount), NormalizedNumber.ZERO)
  const season2TotalPoints = season2.points.reduce((acc, curr) => acc.plus(curr.amount), NormalizedNumber.ZERO)
  const season3TotalPoints = season3.points.reduce((acc, curr) => acc.plus(curr.amount), NormalizedNumber.ZERO)
  const leaderboardPosition = selectSeasonValue(activeSeason, {
    2: season2.leaderboardPosition,
    3: season3.leaderboardPosition,
  })

  return {
    referralCode: season1.referralCode,
    inviterCode: season1.inviterCode,
    invitedUsers: season1.invitedUsers,
    leaderboardPosition,
    points: activeSeasonPoints,
    season1Points: season1.points,
    season2Points: season2.points,
    season3Points: season3.points,
    referralPoints,
    season1TotalPoints,
    season2TotalPoints,
    season3TotalPoints,
  }
}

export interface PointsData {
  referralCode: string
  inviterCode: string | undefined
  invitedUsers: number
  leaderboardPosition: number | null
  points: PointsTypeAmountDetails[]
  season1Points: PointsTypeAmountDetails[]
  season2Points: PointsTypeAmountDetails[]
  season3Points: PointsTypeAmountDetails[]
  referralPoints: NormalizedNumber
  season1TotalPoints: NormalizedNumber
  season2TotalPoints: NormalizedNumber
  season3TotalPoints: NormalizedNumber
}

interface PointsTypeAmountDetails {
  type: PointsType
  amount: NormalizedNumber
  campaignActive: boolean
}

export type UseSparkPointsDataResultOnSuccess = SuspenseQueryWith<{ pointsData: PointsData }>

export function useSparkPointsData({ account }: PointsDataQueryParams): UseSparkPointsDataResultOnSuccess {
  const res = useSuspenseQuery(pointsDataQueryOptions({ account }))

  return {
    ...res,
    pointsData: res.data,
  }
}

function sortByPointsType(a: PointsTypeAmountDetails, b: PointsTypeAmountDetails): number {
  if (a.type === 'staking') return -1
  if (b.type === 'staking') return 1
  if (a.type === 'pendle') return -1
  if (b.type === 'pendle') return 1
  if (a.type === 'referral') return -1
  if (b.type === 'referral') return 1
  if (a.type === 'loyalty') return -1
  if (b.type === 'loyalty') return 1
  if (a.type === 'governance') return -1
  if (b.type === 'governance') return 1
  if (a.type === 'hold-susdc') return 1
  if (b.type === 'hold-susdc') return -1
  return 0
}
